import { InspectionFormModal, InspectionStatusRadio, SharedSpinner } from '@components';
import { useDocumentSasUrl, useInspection } from '@hooks';
import { InspectionResponse } from '@services';
import { Button, Col, Collapse, Image, Row, Space, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

interface IProps {
  inspectionId: string;
}

export const RequestInspectionCollapse: React.FC<IProps> = observer(({ inspectionId }) => {
  const [open, setOpen] = useState(false);
  const { inspection, loading } = useInspection(inspectionId);

  const queryString = useDocumentSasUrl();
  const afterQuestionMark = queryString.split('?')[1];

  const columns: ColumnsType<InspectionResponse> = [
    {
      title: 'Category',
      dataIndex: 'subCategoryItem',
      render: (_, record) => {
        const subCategory = inspection?.category.subCategories?.find((item) =>
          item.items.find((x) => x.id === record.subCategoryItemId)
        );
        const subCategoryItem = subCategory?.items.find((x) => x.id === record.subCategoryItemId);

        const subCategoryName = subCategory?.name ?? '';
        const subCategoryItemName = subCategoryItem?.name ?? '';
        return subCategoryName && subCategoryItemName
          ? `${subCategoryName} - ${subCategoryItemName}`
          : subCategoryName || subCategoryItemName || '-';
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_, record) => (
        <InspectionStatusRadio value={record.status as 'Success' | 'Warning' | 'Failure'} />
      ),
    },
    {
      title: 'Depth',
      dataIndex: 'depth',
      render: (d) => d ?? '-',
    },
    {
      title: 'Images',
      dataIndex: 'documents',
      render: (_, record) => (
        <Space>
          {record.documents?.map((doc) => (
            <Image
              key={doc.id}
              src={`${doc.url}?${afterQuestionMark}`}
              width={50}
              height={50}
              style={{ borderRadius: 6, objectFit: 'cover' }}
            />
          ))}
        </Space>
      ),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      width: 300,
      render: (text) => (
        <Typography.Paragraph ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}>
          {text}
        </Typography.Paragraph>
      ),
    },
  ];

  return (
    <SharedSpinner loading={loading}>
      <Row style={{ marginTop: '20px' }}>
        <Col span={24}>
          <Collapse
            defaultActiveKey={['1']}
            items={[
              {
                key: '1',
                label: (
                  <Row justify="space-between">
                    <Col>{inspection?.category?.name ?? 'Inspection Details'}</Col>
                    <Col>
                      <Button onClick={() => setOpen(true)} type="primary">
                        View full inspection list
                      </Button>
                    </Col>
                  </Row>
                ),
                children: (
                  <Table
                    dataSource={
                      inspection?.responses?.filter(
                        (response) =>
                          response.status !== 'Success' &&
                          (response.status ||
                            response.depth ||
                            (response.documents && response.documents.length > 0) ||
                            response.note)
                      ) ?? []
                    }
                    columns={columns}
                    rowKey="id"
                    pagination={false}
                  />
                ),
              },
            ]}
          />
        </Col>
      </Row>
      {open && (
        <InspectionFormModal
          open={open}
          onCancel={() => setOpen(false)}
          inspectionId={inspectionId}
        />
      )}
    </SharedSpinner>
  );
});
